import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { useGetScheduledContentQuery } from '../documents/getScheduledContent.generated';
import { getCurrentMomentWithoutOffsetInLocalTime } from '../util/dateTime';

import { appClient } from 'src/config/apollo';
import {
    ScheduledContentActivityActivityType,
    ScheduledContentType,
} from 'src/generated/webapp_gql';

import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { formatDailyPauseMoment } from 'src/modules/scheduledContent/util/dateTime';
import { ReactComponent as CheckIcon } from 'src/public/img/dp-check.svg';

import { captureException } from '@sentry/minimal';
import { color, font } from 'src/shared/styles/variables';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { navigationService } from 'src/config/rootService';
import { Dispatch, RootState } from 'src/config/rootStore';
import { ScheduledContentFragment } from 'src/modules/dashboard/documents/getHomeScreen.generated';
import { BaseButton } from 'src/shared/components/Button';
import { ContentBanner } from 'src/shared/ds';

const CardText = styled.p<{ textColor?: string }>(({ textColor }) => ({
    color: textColor ? `#${textColor}` : color.ebonyClay,
}));

const CardTitle = styled(CardText)({
    ...font.heavy(font.size.largeExtended),
    marginBottom: '6px',
});

const CardDate = styled(CardText)({
    ...font.medium(font.size.basePlus),
    marginBottom: '8px',
});

const CardTextContent = styled.div({
    ...font.medium(font.size.medium),
    paddingBottom: '8px',
    flexGrow: 1,
    justifyContent: 'center',
    justifyItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
});

const CardGraphicContainer = styled.div({
    width: '40%',
    height: '100%',
    paddingLeft: '16px',
    justifyContent: 'flex-end',
    paddingTop: 16,
    opacity: 1,
    display: 'flex',
    alignItems: 'flex-end',
});

const CardContentContainer = styled.div({
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '16px',
    marginTop: '16px',
    paddingBottom: '16px',
});

const CardBackground = styled.div<{
    accentColor?: string;
}>(({ accentColor }) => {
    const computedAccentColor = accentColor ? `#${accentColor}` : color.white;

    return {
        backgroundColor: computedAccentColor,
    };
});

const CardContainer = styled(CardBackground)({
    display: 'flex',
    flexDirection: 'row',
    flex: '2 0 calc(60% - 20px)',
    flexWrap: 'nowrap',
    borderRadius: 8,
    overflow: 'hidden',
    marginRight: 10,
    marginBottom: 10,
    marginLeft: 10,
});

export enum DailyPauseSkeletonCardTestID {
    Container = 'DailyPauseSkeletonCardTestID.Container',
}

export const DailyPauseSkeletonCard: React.FC = () => {
    return (
        <CardContainer
            data-testid={DailyPauseSkeletonCardTestID.Container}
            accentColor={color.whiteLilac}
        >
            <CardContentContainer></CardContentContainer>
        </CardContainer>
    );
};

export enum DailyPauseCardTestID {
    Container = 'DailyPauseCardTestID.Container',
    Button = 'DailyPauseCardTestID.Button',
    Graphic = 'DailyPauseCardTestID.Graphic',
    TextDate = 'DailyPauseCardTestID.TextDate',
    TextTitle = 'DailyPauseCardTestID.TextTitle',
    TextContent = 'DailyPauseCardTestID.TextContent',
}

const DailyPauseButton = styled<
    typeof BaseButton,
    {
        isCompleted: boolean;
        textColor?: string;
        accentColor?: string;
    }
>(BaseButton)(({ textColor, accentColor, isCompleted }) => {
    const computedTextColor = textColor ? `#${textColor}` : color.ebonyClay;
    const computedAccentColor = accentColor ? `#${accentColor}` : color.white;
    const _textColor = isCompleted ? computedTextColor : color.ebonyClay;
    const _backgroundColor = isCompleted ? computedAccentColor : color.white;

    return {
        '&&': {
            borderColor: color.white,
            color: _textColor,
            backgroundColor: _backgroundColor,
        },
        '&:hover': {
            color: color.ebonyClay,
            backgroundColor: color.white,
            // Handles the "check" icon on hover.
            'svg path': {
                fill: color.ebonyClay,
            },
        },
        borderWidth: 2,
        marginTop: 16,
        height: 42,
        span: {
            ...font.heavy(font.size.medium),
        },
    };
});

interface Props {
    /**
     * The UUID of the pause being rendered.
     *
     * (Used to route to the pause on click)
     */
    uuid: string;
    svgUri?: string | null;
    svgXml?: string | null;
    title: string;
    content: string;
    /**
     * Expects a hex value (FFF or EFEFEF) without the
     * `#` prefix.
     */
    accentColor?: string;
    /**
     * Expects a hex value (FFF or EFEFEF) without the
     * `#` prefix.
     */
    textColor?: string;
    /**
     * Whether the card should render the CTA as "Completed"
     */
    isCompleted: boolean;
    currentMoment?: moment.Moment;
}

export const DailyPauseCardComponent: React.FC<Props> = ({
    accentColor,
    textColor,
    svgUri,
    title,
    content,
    isCompleted,
    currentMoment,
    uuid,
}) => {
    const { t, i18n } = useTranslation();
    const flags = useFlags();

    const ctaButtonTitle = isCompleted
        ? t('dailyPause:content_banner.completed_button_text')
        : t('dailyPause:content_banner.button_text');

    if (flags?.homeScreenRevampPhase1Web) {
        return (
            <ContentBanner
                title={title}
                description={content}
                image={svgUri ?? undefined}
                subtitle={currentMoment && formatDailyPauseMoment(currentMoment, i18n.language)}
                buttonTitle={ctaButtonTitle}
                onClick={() =>
                    navigationService.navigate('/pause', {
                        uuid,
                    })
                }
                backgroundColor={accentColor ? `#${accentColor}` : color.white}
                buttonProps={{
                    startIcon: isCompleted ? 'checkmark' : undefined,
                    variant: 'secondary',
                    completed: isCompleted,
                }}
            />
        );
    }

    return (
        <CardContainer accentColor={accentColor} data-testid={DailyPauseCardTestID.Container}>
            <CardContentContainer>
                <CardTextContent>
                    {currentMoment ? (
                        <CardDate textColor={textColor} data-testid={DailyPauseCardTestID.TextDate}>
                            {formatDailyPauseMoment(currentMoment, i18n.language)}
                        </CardDate>
                    ) : null}
                    <CardTitle textColor={textColor} data-testid={DailyPauseCardTestID.TextTitle}>
                        {title}
                    </CardTitle>
                    <CardText textColor={textColor} data-testid={DailyPauseCardTestID.TextContent}>
                        {content}
                    </CardText>
                    <div>
                        <DailyPauseButton
                            // TODO: Figure out why Emotion is typing 'theme' as required here.
                            theme={{}}
                            testID={DailyPauseCardTestID.Button}
                            isPill={true}
                            horizontalPadding="small"
                            accentColor={accentColor}
                            textColor={textColor}
                            isCompleted={isCompleted}
                            to={{
                                pathname: '/pause',
                                state: {
                                    uuid,
                                },
                            }}
                        >
                            {isCompleted ? (
                                <CheckIcon
                                    style={{
                                        height: 12,
                                        width: 14,
                                        verticalAlign: 'middle',
                                        marginRight: 12,
                                    }}
                                />
                            ) : null}
                            {ctaButtonTitle}
                        </DailyPauseButton>
                    </div>
                </CardTextContent>
            </CardContentContainer>
            <CardGraphicContainer>
                {svgUri && <img data-testid={DailyPauseCardTestID.Graphic} src={svgUri} />}
            </CardGraphicContainer>
        </CardContainer>
    );
};

export const DailyPauseCard: React.FC<{
    fallback: JSX.Element;
}> = ({ fallback }) => {
    const dispatch = useDispatch<Dispatch>();
    const uuid = useSelector<RootState, string | null>(
        state => state.scheduledContent.currentDailyPauseUuid,
    );
    const [currentMoment] = useState(getCurrentMomentWithoutOffsetInLocalTime);

    const { data, error, loading, refetch } = useGetScheduledContentQuery({
        client: appClient,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: false,
        variables: {
            contentType: ScheduledContentType.DailyPause,
            // If we have a cached UUID, we query that to ensure Daily Pause
            // is loaded from Apollo Cache first. After this, we refetch
            // in a useEffect to get the current Daily Pause.
            ...(uuid ? { uuid } : { forTime: currentMoment }),
        },
    });

    // Fetch the latest Daily Pause...
    useEffect(() => {
        refetch({
            contentType: ScheduledContentType.DailyPause,
            forTime: currentMoment.toISOString(),
        });
    }, [currentMoment]);

    // Store the current Daily Pause UUID in global state to enable
    // cached lookups.
    useEffect(() => {
        if (!data?.scheduledContent?.uuid) {
            return;
        }

        dispatch.scheduledContent.setCurrentDailyPauseUuid(data.scheduledContent.uuid);
    }, [data]);

    useEffect(() => {
        if (error) {
            captureException(error);
        }
    }, [error]);

    if (loading && !data) {
        return <DailyPauseSkeletonCard />;
    }

    // If there...
    // - Is no data (likely an error occured)
    // - Is no daily pause (scheduledContent is null)
    // - Is an error (couldn't load)
    // We show a fallback component instead.
    if (!data || !data.scheduledContent) {
        return fallback;
    }

    const { scheduledContent } = data;

    const isCompleted = scheduledContent.myActivity?.some(
        activity => activity?.activityType == ScheduledContentActivityActivityType.Completed,
    );

    return (
        <DailyPauseCardComponent
            uuid={scheduledContent.uuid}
            svgUri={scheduledContent.bannerImage}
            title={scheduledContent.bannerTitle}
            currentMoment={currentMoment}
            content={scheduledContent.bannerContent}
            textColor={scheduledContent.titleColor ?? undefined}
            accentColor={scheduledContent.accentColor ?? undefined}
            isCompleted={isCompleted ?? false}
        />
    );
};

export const NewDailyPauseCard: React.FC<{
    scheduledContent: ScheduledContentFragment;
}> = ({ scheduledContent }) => {
    const [currentMoment] = useState(getCurrentMomentWithoutOffsetInLocalTime);

    const isCompleted = scheduledContent.myActivity?.some(
        activity => activity?.activityType == ScheduledContentActivityActivityType.Completed,
    );

    return (
        <DailyPauseCardComponent
            uuid={scheduledContent.uuid}
            svgUri={scheduledContent.bannerImage}
            title={scheduledContent.bannerTitle}
            currentMoment={currentMoment}
            content={scheduledContent.bannerContent}
            textColor={scheduledContent.titleColor ?? undefined}
            accentColor={scheduledContent.accentColor ?? undefined}
            isCompleted={isCompleted ?? false}
        />
    );
};
